'use client';

import { c as _c } from "react/compiler-runtime";
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { segmentClassName, segmentedTrackClassName } from '@/components/ui/segmented-control';
import { Skeleton } from '@/components/ui/skeleton';
import type { MaterialOrderForm, MaterialOrderItem } from '@/features/hof-dashboard/api/material-orders';
import type { MaterialExportSheet } from '@/features/hof-dashboard/api/material-orders-export';
import { LABEL_CLASS, Panel, PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS, SectionHeading } from '@/features/hof-dashboard/components/dashboard-ui';
import { MaterialOrderReview } from '@/features/hof-dashboard/components/material-order-review';
import { SortableHeader } from '@/features/hof-dashboard/components/sortable-header';
import { useMaterialExport } from '@/features/hof-dashboard/hooks/use-material-export';
import { sortRows, useTableSort } from '@/features/hof-dashboard/hooks/use-table-sort';
import { formatDate, formatDateTime, formatNumber, translate, type TextKey } from '@/features/hof-dashboard/texts';
import { trpc } from '@/trpc/client';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { ChevronDown, Download, FileSpreadsheet } from 'lucide-react';
import type React from 'react';
import { useId, useRef, useState } from 'react';
type View = 'total' | 'perHof';
const SHEET_LABELS: Record<MaterialExportSheet, TextKey> = {
  total: 'viewTotal',
  perHof: 'viewPerHof',
  remarks: 'remarks',
  versions: 'sheetVersions'
};

/** A choice of a few, as the app's segmented control. */
const Segmented = t0 => {
  const $ = _c(11);
  const {
    label,
    options,
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(segmentedTrackClassName, "inline-flex");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onChange || $[2] !== options || $[3] !== value) {
    let t3;
    if ($[5] !== onChange || $[6] !== value) {
      t3 = option => <button key={option.value} type="button" aria-pressed={option.value === value} className={cn(segmentClassName(option.value === value), "px-4 text-sm font-semibold")} onClick={() => onChange(option.value)}>{option.label}</button>;
      $[5] = onChange;
      $[6] = value;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = options.map(t3);
    $[1] = onChange;
    $[2] = options;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[8] !== label || $[9] !== t2) {
    t3 = <div role="group" aria-label={label} className={t1}>{t2}</div>;
    $[8] = label;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};

/** The materials by the form's sections, in its order. */
const bySection = (items: readonly MaterialOrderItem[]): {
  section: string | undefined;
  items: MaterialOrderItem[];
}[] => {
  const sections: {
    section: string | undefined;
    items: MaterialOrderItem[];
  }[] = [];
  for (const item of items) {
    const last = sections.at(-1);
    if (last !== undefined && last.section === item.section) last.items.push(item);else sections.push({
      section: item.section,
      items: [item]
    });
  }
  return sections;
};

/** The Höfe that ordered a material and how much, the largest first. */
const ordersOf = (form: MaterialOrderForm, itemId: string): {
  hofId: string;
  hofName: string;
  quantity: number;
}[] => form.hoefe.map(hof => ({
  hofId: hof.hofId,
  hofName: hof.hofName,
  quantity: hof.quantities[itemId] ?? 0
})).filter(order => order.quantity > 0).toSorted((a, b) => b.quantity - a.quantity);

/** Every Hof's quantity of one material as bars, so an outlier stands out. */
const HofBars: React.FC<{
  orders: {
    hofId: string;
    hofName: string;
    quantity: number;
  }[];
  onOpen: (hofId: string) => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(11);
  const {
    orders,
    onOpen,
    locale
  } = t0;
  const largest = orders[0]?.quantity ?? 0;
  let t1;
  if ($[0] !== largest || $[1] !== locale || $[2] !== onOpen || $[3] !== orders) {
    let t2;
    if ($[5] !== largest || $[6] !== locale || $[7] !== onOpen) {
      t2 = order => <li key={order.hofId} className="grid grid-cols-[minmax(8rem,16rem)_minmax(0,1fr)_4rem] items-center gap-3"><button type="button" className="min-h-9 cursor-pointer truncate text-left text-sm text-green-900 hover:underline @min-[900px]/dashboard:pointer-fine:min-h-[28px] @min-[900px]/dashboard:pointer-fine:text-[14px]" aria-label={translate("openOrder", locale, {
          hof: order.hofName
        })} onClick={() => onOpen(order.hofId)}>{order.hofName}</button><span aria-hidden={true} className="bg-quiet h-2.5 overflow-hidden rounded-full"><span className="bg-conveniat-green block h-full rounded-full" style={{
            width: `${order.quantity / largest * 100}%`
          }} /></span><span className="text-right text-sm font-semibold text-green-900 tabular-nums @min-[900px]/dashboard:pointer-fine:text-[14px]">{formatNumber(order.quantity, locale)}</span></li>;
      $[5] = largest;
      $[6] = locale;
      $[7] = onOpen;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = orders.map(t2);
    $[0] = largest;
    $[1] = locale;
    $[2] = onOpen;
    $[3] = orders;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = <ul className="space-y-1.5">{t1}</ul>;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};

/** A row's 44px target, which a mouse does not need: on a desktop the rows sit closer. */
const DESKTOP_ROW = '@min-[900px]/dashboard:pointer-fine:min-h-[32px]';

/** The line under a sticky header, so rows scrolling under it stay apart from it. */
const STICKY_HEAD = 'sticky z-20 bg-page shadow-[0_1px_0] shadow-line';

/** The line beside a sticky first column, for rows scrolled sideways under it. */
const STICKY_COLUMN = 'sticky left-0 z-10 shadow-[1px_0_0] shadow-line';
type TotalColumn = 'material' | 'total' | 'hoefe' | 'largest';

/** One material with what the Höfe ordered of it. */
interface MaterialTotal {
  item: MaterialOrderItem;
  orders: {
    hofId: string;
    hofName: string;
    quantity: number;
  }[];
  total: number;
}
const totalValue = (row: MaterialTotal, column: TotalColumn): number | string => {
  if (column === 'material') return row.item.name;
  if (column === 'total') return row.total;
  if (column === 'hoefe') return row.orders.length;
  return row.orders[0]?.quantity ?? 0;
};

/**
 * How much of each material the Ressort has to get, and from whom how much. In the form's order
 * by its sections, or sorted by any column, then as one list.
 */
const TotalTable: React.FC<{
  form: MaterialOrderForm;
  onOpen: (hofId: string) => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(21);
  const {
    form,
    onOpen,
    locale
  } = t0;
  const [expanded, setExpanded] = useState();
  const {
    sort,
    toggle
  } = useTableSort();
  let t1;
  if ($[0] !== form) {
    t1 = items => items.map(item => {
      const orders = ordersOf(form, item.id);
      return {
        item,
        orders,
        total: orders.reduce(_temp, 0)
      };
    });
    $[0] = form;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const totals = t1;
  let t2;
  if ($[2] !== expanded || $[3] !== form.items || $[4] !== locale || $[5] !== onOpen || $[6] !== sort || $[7] !== toggle || $[8] !== totals) {
    const groups = sort === undefined ? bySection(form.items).map(t3 => {
      const {
        section,
        items: items_0
      } = t3;
      return {
        section,
        rows: totals(items_0)
      };
    }) : [{
      section: undefined,
      rows: sortRows(totals(form.items), sort, totalValue, locale)
    }];
    let t4;
    if ($[10] !== locale || $[11] !== sort || $[12] !== toggle) {
      t4 = (column, label, className) => <SortableHeader sortKey={column} sort={sort} onSort={toggle} align={column === "material" ? "left" : "right"} className={className}>{translate(label, locale)}</SortableHeader>;
      $[10] = locale;
      $[11] = sort;
      $[12] = toggle;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    const head = t4;
    let t5;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = cn(STICKY_HEAD, "top-[calc(var(--app-header-height)+var(--offline-banner-height,0px)+var(--page-bar-height,0px))]");
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== head) {
      t6 = <thead className={t5}><tr className={LABEL_CLASS}>{head("material", "material", "rounded-tl-[14px] pl-2 @xl:pl-3")}{head("total", "xlsxTotal")}{head("hoefe", "xlsxHoefe")}{head("largest", "largestOrder", "rounded-tr-[14px] pr-2 @xl:pr-3")}</tr></thead>;
      $[15] = head;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== expanded || $[18] !== locale || $[19] !== onOpen) {
      t7 = (t8, index) => {
        const {
          section: section_0,
          rows
        } = t8;
        return <tbody key={section_0 ?? `section-${index}`} className="divide-line divide-y">{section_0 !== undefined && <tr><th scope="colgroup" colSpan={4} className={cn(LABEL_CLASS, "border-line border-t px-5 pt-5 pb-2 text-left @xl:px-6 @min-[900px]/dashboard:pointer-fine:pt-[16px] @min-[900px]/dashboard:pointer-fine:pb-[6px]")}>{section_0}</th></tr>}{rows.map(t9 => {
            const {
              item: item_0,
              orders: orders_0,
              total
            } = t9;
            const open = expanded === item_0.id;
            return [<tr key={item_0.id} className={cn(open && "bg-gray-50")}><th scope="row" className="px-5 py-1 text-left font-normal @xl:px-6 @min-[900px]/dashboard:pointer-fine:py-0">{orders_0.length === 0 ? <span className={cn("flex min-h-11 items-center text-green-700", DESKTOP_ROW)}>{item_0.name}</span> : <button type="button" aria-expanded={open} aria-label={translate("showHoefeOf", locale, {
                  material: item_0.name
                })} className={cn("flex min-h-11 w-full cursor-pointer items-center gap-2 text-left text-green-900", DESKTOP_ROW)} onClick={() => setExpanded(open ? undefined : item_0.id)}><ChevronDown aria-hidden={true} className={cn("h-4 w-4 shrink-0 text-green-500 transition", open && "rotate-180")} />{item_0.name}</button>}</th><td className={cn("px-3 text-right font-semibold tabular-nums", total === 0 ? "text-gray-400" : "text-green-900")}>{formatNumber(total, locale)}</td><td className="px-3 text-right text-green-800 tabular-nums">{orders_0.length}</td><td className="px-5 text-right text-green-800 tabular-nums @xl:px-6">{orders_0[0] === undefined ? "\u2013" : formatNumber(orders_0[0].quantity, locale)}</td></tr>, open && <tr key={`${item_0.id}-bars`} className="bg-gray-50"><td colSpan={4} className="px-5 pt-1 pb-4 @xl:px-6"><HofBars orders={orders_0} onOpen={onOpen} locale={locale} /></td></tr>];
          })}</tbody>;
      };
      $[17] = expanded;
      $[18] = locale;
      $[19] = onOpen;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    t2 = <table className="w-full text-base @min-[900px]/dashboard:pointer-fine:text-[15px]">{t6}{groups.map(t7)}</table>;
    $[2] = expanded;
    $[3] = form.items;
    $[4] = locale;
    $[5] = onOpen;
    $[6] = sort;
    $[7] = toggle;
    $[8] = totals;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};

/** The Hof's name, or one material's quantity, to sort the Höfe by. */
type PerHofColumn = 'hof' | `item:${string}`;

/**
 * What each Hof gets at the handout: one row per Hof, one column per material, sortable by the
 * Hof or by any material. Scrolls in a box of its own, both ways, with the materials on top and
 * the Höfe on the left staying in place.
 */
const PerHofTable: React.FC<{
  form: MaterialOrderForm;
  onOpen: (hofId: string) => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(62);
  const {
    form,
    onOpen,
    locale
  } = t0;
  const {
    sort,
    toggle
  } = useTableSort();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== form.hoefe || $[1] !== form.items || $[2] !== locale || $[3] !== onOpen || $[4] !== sort || $[5] !== toggle) {
    const sections = bySection(form.items);
    const hasSections = sections.some(_temp2);
    const hoefe = sortRows(form.hoefe, sort, _temp3, locale);
    t5 = "max-h-[calc(100dvh-var(--app-header-height)-2rem)] overflow-auto overscroll-x-contain";
    t3 = "w-max min-w-full text-sm @min-[900px]/dashboard:pointer-fine:text-[14px]";
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = cn(STICKY_HEAD, "top-0");
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    const t7 = hasSections && <tr className={LABEL_CLASS}><th scope="col" className={cn(STICKY_COLUMN, "bg-page")} />{sections.map(_temp4)}</tr>;
    let t8;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = cn(STICKY_COLUMN, "bg-page pl-2", LABEL_CLASS);
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] !== locale) {
      t9 = translate("hof", locale);
      $[13] = locale;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    let t10;
    if ($[15] !== sort || $[16] !== t9 || $[17] !== toggle) {
      t10 = <SortableHeader sortKey="hof" sort={sort} onSort={toggle} className={t8}>{t9}</SortableHeader>;
      $[15] = sort;
      $[16] = t9;
      $[17] = toggle;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    let t11;
    if ($[19] !== form.items || $[20] !== sort || $[21] !== toggle) {
      let t12;
      if ($[23] !== sort || $[24] !== toggle) {
        t12 = item => <SortableHeader key={item.id} sortKey={`item:${item.id}`} sort={sort} onSort={toggle} align="right" className="max-w-32 min-w-20 text-xs font-semibold [overflow-wrap:anywhere] hyphens-auto text-green-800 @min-[900px]/dashboard:pointer-fine:text-[12px]">{item.name}</SortableHeader>;
        $[23] = sort;
        $[24] = toggle;
        $[25] = t12;
      } else {
        t12 = $[25];
      }
      t11 = form.items.map(t12);
      $[19] = form.items;
      $[20] = sort;
      $[21] = toggle;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[26] !== t10 || $[27] !== t11) {
      t12 = <tr>{t10}{t11}</tr>;
      $[26] = t10;
      $[27] = t11;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    if ($[29] !== t12 || $[30] !== t7) {
      t4 = <thead className={t6}>{t7}{t12}</thead>;
      $[29] = t12;
      $[30] = t7;
      $[31] = t4;
    } else {
      t4 = $[31];
    }
    t1 = "divide-line divide-y";
    let t13;
    if ($[32] !== form.items || $[33] !== locale || $[34] !== onOpen) {
      t13 = hof_0 => <tr key={hof_0.hofId} className="group"><th scope="row" className={cn(STICKY_COLUMN, "bg-white px-5 py-1 text-left font-normal group-hover:bg-gray-50 @min-[900px]/dashboard:pointer-fine:py-0")}><button type="button" className={cn("flex min-h-11 max-w-60 cursor-pointer items-center gap-2 text-left text-green-900 hover:underline", DESKTOP_ROW)} aria-label={translate("openOrder", locale, {
            hof: hof_0.hofName
          })} onClick={() => onOpen(hof_0.hofId)}>{hof_0.hofName}{hof_0.orderedAt === undefined && <span className="text-xs text-green-600">({translate("legendMissing", locale)})</span>}</button></th>{form.items.map(item_0 => {
          const quantity = hof_0.quantities[item_0.id] ?? 0;
          return <td key={item_0.id} className={cn("px-3 text-right tabular-nums group-hover:bg-gray-50", quantity === 0 ? "text-gray-300" : "text-green-900")}>{quantity === 0 ? "\u2013" : formatNumber(quantity, locale)}</td>;
        })}</tr>;
      $[32] = form.items;
      $[33] = locale;
      $[34] = onOpen;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    t2 = hoefe.map(t13);
    $[0] = form.hoefe;
    $[1] = form.items;
    $[2] = locale;
    $[3] = onOpen;
    $[4] = sort;
    $[5] = toggle;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
  }
  let t6;
  if ($[36] !== t1 || $[37] !== t2) {
    t6 = <tbody className={t1}>{t2}</tbody>;
    $[36] = t1;
    $[37] = t2;
    $[38] = t6;
  } else {
    t6 = $[38];
  }
  let t7;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn(STICKY_COLUMN, "bg-white px-5 py-3 text-left text-green-900 @min-[900px]/dashboard:pointer-fine:py-[8px]");
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  let t8;
  if ($[40] !== locale) {
    t8 = translate("xlsxTotal", locale);
    $[40] = locale;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== t8) {
    t9 = <th scope="row" className={t7}>{t8}</th>;
    $[42] = t8;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  let t10;
  if ($[44] !== form.hoefe || $[45] !== form.items || $[46] !== locale) {
    let t11;
    if ($[48] !== form.hoefe || $[49] !== locale) {
      t11 = item_1 => <td key={item_1.id} className="px-3 py-3 text-right text-green-900 tabular-nums @min-[900px]/dashboard:pointer-fine:py-[8px]">{formatNumber(form.hoefe.reduce((sum, hof_1) => sum + (hof_1.quantities[item_1.id] ?? 0), 0), locale)}</td>;
      $[48] = form.hoefe;
      $[49] = locale;
      $[50] = t11;
    } else {
      t11 = $[50];
    }
    t10 = form.items.map(t11);
    $[44] = form.hoefe;
    $[45] = form.items;
    $[46] = locale;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[51] !== t10 || $[52] !== t9) {
    t11 = <tfoot className="shadow-line sticky bottom-0 z-20 bg-white shadow-[0_-1px_0]"><tr className="font-semibold">{t9}{t10}</tr></tfoot>;
    $[51] = t10;
    $[52] = t9;
    $[53] = t11;
  } else {
    t11 = $[53];
  }
  let t12;
  if ($[54] !== t11 || $[55] !== t3 || $[56] !== t4 || $[57] !== t6) {
    t12 = <table className={t3}>{t4}{t6}{t11}</table>;
    $[54] = t11;
    $[55] = t3;
    $[56] = t4;
    $[57] = t6;
    $[58] = t12;
  } else {
    t12 = $[58];
  }
  let t13;
  if ($[59] !== t12 || $[60] !== t5) {
    t13 = <div className={t5}>{t12}</div>;
    $[59] = t12;
    $[60] = t5;
    $[61] = t13;
  } else {
    t13 = $[61];
  }
  return t13;
};

/**
 * What the export holds, both forms or the one shown, and which sheets: a popup under its button
 * on a desktop, a sheet from the bottom on a phone.
 */
const ExportSheet: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The button that opens it, which a desktop shows it under. */
  anchor: React.RefObject<HTMLButtonElement | null>;
  forms: readonly MaterialOrderForm[];
  current: MaterialOrderForm;
  locale: Locale;
}> = t0 => {
  const $ = _c(55);
  const {
    open,
    onOpenChange,
    anchor,
    forms,
    current,
    locale
  } = t0;
  const {
    download,
    pending
  } = useMaterialExport(locale);
  const [onlyCurrent, setOnlyCurrent] = useState(false);
  const [sheets, setSheets] = useState(_temp5);
  const formsLabelId = useId();
  const sheetsLabelId = useId();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = sheet => setSheets(chosen => {
      const next = new Set(chosen);
      if (next.has(sheet)) {
        next.delete(sheet);
      } else {
        next.add(sheet);
      }
      return next;
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const toggle = t1;
  let t2;
  if ($[1] !== locale) {
    t2 = translate("excelExport", locale);
    $[1] = locale;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(PRIMARY_BUTTON_CLASS, "w-full");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = pending || sheets.size === 0;
  let t5;
  if ($[4] !== current || $[5] !== download || $[6] !== forms || $[7] !== onOpenChange || $[8] !== onlyCurrent || $[9] !== sheets) {
    t5 = () => void download(onlyCurrent ? [current.id] : forms.map(_temp6), [...sheets]).then(() => onOpenChange(false));
    $[4] = current;
    $[5] = download;
    $[6] = forms;
    $[7] = onOpenChange;
    $[8] = onlyCurrent;
    $[9] = sheets;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Download aria-hidden={true} />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = pending ? "exporting" : "download";
  let t8;
  if ($[12] !== locale || $[13] !== t7) {
    t8 = translate(t7, locale);
    $[12] = locale;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t4 || $[16] !== t5 || $[17] !== t8) {
    t9 = <button type="button" className={t3} disabled={t4} onClick={t5}>{t6}{t8}</button>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = sheets.size === 0 ? "exportNoSheet" : "exportHint";
  let t11;
  if ($[19] !== locale || $[20] !== t10) {
    t11 = translate(t10, locale);
    $[19] = locale;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <p className="text-sm text-pretty text-green-700">{t11}</p>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t9) {
    t13 = <div className="space-y-2">{t9}{t12}</div>;
    $[24] = t12;
    $[25] = t9;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== current || $[28] !== forms.length || $[29] !== formsLabelId || $[30] !== locale || $[31] !== onlyCurrent) {
    t14 = forms.length > 1 && <fieldset aria-labelledby={formsLabelId} className="space-y-2"><legend id={formsLabelId} className="text-sm font-semibold text-green-900">{translate("exportForms", locale)}</legend>{[false, true].map(only => <label key={String(only)} className="flex min-h-11 items-center gap-3 text-base"><input type="radio" name={formsLabelId} checked={onlyCurrent === only} onChange={() => setOnlyCurrent(only)} className="accent-conveniat-green h-4 w-4 shrink-0" />{only ? translate("exportCurrentForm", locale, {
          form: current.title
        }) : translate("exportAllForms", locale)}</label>)}</fieldset>;
    $[27] = current;
    $[28] = forms.length;
    $[29] = formsLabelId;
    $[30] = locale;
    $[31] = onlyCurrent;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== locale) {
    t15 = translate("exportSheets", locale);
    $[33] = locale;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== sheetsLabelId || $[36] !== t15) {
    t16 = <legend id={sheetsLabelId} className="text-sm font-semibold text-green-900">{t15}</legend>;
    $[35] = sheetsLabelId;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== locale || $[39] !== sheets) {
    t17 = (Object.keys(SHEET_LABELS) as MaterialExportSheet[]).map(sheet_0 => <label key={sheet_0} className="flex min-h-11 items-center gap-3 text-base"><input type="checkbox" checked={sheets.has(sheet_0)} onChange={() => toggle(sheet_0)} className="accent-conveniat-green h-4 w-4 shrink-0" />{translate(SHEET_LABELS[sheet_0], locale)}</label>);
    $[38] = locale;
    $[39] = sheets;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== sheetsLabelId || $[42] !== t16 || $[43] !== t17) {
    t18 = <fieldset aria-labelledby={sheetsLabelId} className="space-y-2">{t16}{t17}</fieldset>;
    $[41] = sheetsLabelId;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t14 || $[46] !== t18) {
    t19 = <div className="grid gap-5 sm:grid-cols-2">{t14}{t18}</div>;
    $[45] = t14;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== anchor || $[49] !== onOpenChange || $[50] !== open || $[51] !== t13 || $[52] !== t19 || $[53] !== t2) {
    t20 = <BottomSheet open={open} onOpenChange={onOpenChange} anchor={anchor} title={t2} footer={t13}>{t19}</BottomSheet>;
    $[48] = anchor;
    $[49] = onOpenChange;
    $[50] = open;
    $[51] = t13;
    $[52] = t19;
    $[53] = t2;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  return t20;
};

/** A Hof's name that opens its order. */
const HofLink: React.FC<{
  name: string;
  onOpen: () => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(7);
  const {
    name,
    onOpen,
    locale
  } = t0;
  let t1;
  if ($[0] !== locale || $[1] !== name) {
    t1 = translate("openOrder", locale, {
      hof: name
    });
    $[0] = locale;
    $[1] = name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== name || $[4] !== onOpen || $[5] !== t1) {
    t2 = <button type="button" className="cursor-pointer text-left font-semibold text-green-900 underline-offset-2 hover:underline" aria-label={t1} onClick={onOpen}>{name}</button>;
    $[3] = name;
    $[4] = onOpen;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

/** Who ordered and who did not, what changed since the export, and the Höfe's remarks. */
const SidePanels: React.FC<{
  form: MaterialOrderForm;
  onOpen: (hofId: string) => void;
  className?: string;
  locale: Locale;
}> = t0 => {
  const $ = _c(52);
  const {
    form,
    onOpen,
    className,
    locale
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== className || $[1] !== form.exportedAt || $[2] !== form.hoefe || $[3] !== form.mode || $[4] !== locale || $[5] !== onOpen) {
    const ordered = form.hoefe.filter(_temp7);
    const missing = form.hoefe.filter(_temp8);
    const changed = ordered.filter(_temp9).toSorted(_temp10);
    let t7;
    if ($[13] !== form.mode) {
      t7 = hof_2 => {
        const counted = form.mode === "entries" ? hof_2.versions : hof_2.versions.slice(0, 1);
        const all = counted.flatMap(_temp11);
        return all.length === 0 ? [] : [{
          hof: hof_2,
          remarks: all
        }];
      };
      $[13] = form.mode;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    const remarks = form.hoefe.flatMap(t7);
    if ($[15] !== className) {
      t4 = cn("space-y-6", className);
      $[15] = className;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    let t8;
    if ($[17] !== locale) {
      t8 = translate("orderedState", locale);
      $[17] = locale;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[19] !== t8) {
      t9 = <SectionHeading className="text-xl">{t8}</SectionHeading>;
      $[19] = t8;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    const t10 = <p className="text-base text-green-800">{translate("orderedCount", locale, {
        n: ordered.length,
        total: form.hoefe.length
      })}</p>;
    const t11 = missing.length === 0 ? <p className="text-sm text-green-700">{translate("allOrdered", locale)}</p> : <details className="group text-sm"><summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 font-semibold text-green-900"><ChevronDown aria-hidden={true} className="h-4 w-4 transition group-open:rotate-180" />{translate("notOrderedCount", locale, {
          n: missing.length
        })}</summary><ul className="space-y-1 pl-6 text-green-800">{missing.map(_temp12)}</ul></details>;
    if ($[21] !== t10 || $[22] !== t11 || $[23] !== t9) {
      t5 = <Panel className="space-y-3">{t9}{t10}{t11}</Panel>;
      $[21] = t10;
      $[22] = t11;
      $[23] = t9;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    let t12;
    if ($[25] !== locale) {
      t12 = translate("changedSinceExport", locale);
      $[25] = locale;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    let t13;
    if ($[27] !== t12) {
      t13 = <SectionHeading className="text-xl">{t12}</SectionHeading>;
      $[27] = t12;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    let t14;
    if ($[29] !== form.exportedAt || $[30] !== locale) {
      t14 = form.exportedAt === undefined ? translate("neverExported", locale) : translate("lastExportOn", locale, {
        date: formatDateTime(form.exportedAt, locale)
      });
      $[29] = form.exportedAt;
      $[30] = locale;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== t14) {
      t15 = <p className="text-sm text-green-700">{t14}</p>;
      $[32] = t14;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    const t16 = form.exportedAt !== undefined && (changed.length === 0 ? <p className="text-base text-green-800">{translate("nothingChanged", locale)}</p> : <ul className="divide-line divide-y text-base">{changed.map(hof_4 => <li key={hof_4.hofId} className="flex items-baseline justify-between gap-3 py-2"><HofLink name={hof_4.hofName} onOpen={() => onOpen(hof_4.hofId)} locale={locale} /><span className="shrink-0 text-sm text-green-700 tabular-nums">{formatDate(hof_4.orderedAt ?? "", locale)}</span></li>)}</ul>);
    if ($[34] !== t13 || $[35] !== t15 || $[36] !== t16) {
      t6 = <Panel className="space-y-3">{t13}{t15}{t16}</Panel>;
      $[34] = t13;
      $[35] = t15;
      $[36] = t16;
      $[37] = t6;
    } else {
      t6 = $[37];
    }
    T0 = Panel;
    t1 = "space-y-3";
    let t17;
    if ($[38] !== locale) {
      t17 = translate("remarks", locale);
      $[38] = locale;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    if ($[40] !== t17) {
      t2 = <SectionHeading className="text-xl">{t17}</SectionHeading>;
      $[40] = t17;
      $[41] = t2;
    } else {
      t2 = $[41];
    }
    t3 = remarks.length === 0 ? <p className="text-sm text-green-700">{translate("noRemarks", locale)}</p> : <ul className="divide-line divide-y">{remarks.map(t18 => {
        const {
          hof: hof_5,
          remarks: ofHof
        } = t18;
        return <li key={hof_5.hofId} className="space-y-1 py-3 first:pt-0 last:pb-0"><HofLink name={hof_5.hofName} onOpen={() => onOpen(hof_5.hofId)} locale={locale} />{ofHof.map(_temp13)}</li>;
      })}</ul>;
    $[0] = className;
    $[1] = form.exportedAt;
    $[2] = form.hoefe;
    $[3] = form.mode;
    $[4] = locale;
    $[5] = onOpen;
    $[6] = T0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    T0 = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[42] !== T0 || $[43] !== t1 || $[44] !== t2 || $[45] !== t3) {
    t7 = <T0 className={t1}>{t2}{t3}</T0>;
    $[42] = T0;
    $[43] = t1;
    $[44] = t2;
    $[45] = t3;
    $[46] = t7;
  } else {
    t7 = $[46];
  }
  let t8;
  if ($[47] !== t4 || $[48] !== t5 || $[49] !== t6 || $[50] !== t7) {
    t8 = <div className={t4}>{t5}{t6}{t7}</div>;
    $[47] = t4;
    $[48] = t5;
    $[49] = t6;
    $[50] = t7;
    $[51] = t8;
  } else {
    t8 = $[51];
  }
  return t8;
};

/**
 * The material orders of every Hof, for the Ressort: per form either how much of each material
 * is needed, with every Hof's share behind a row, or what each Hof gets, with who has not
 * ordered, what changed since the last export and the Höfe's remarks beside it, and the Excel
 * export. Only what counts is added up: the newest version of each Hof's order. A Hof opens its
 * order for review.
 */
export const MaterialOrdersView: React.FC<{
  onOpenHof: (hofId: string) => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(103);
  const {
    onOpenHof,
    locale
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      staleTime: 30000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      meta: {
        persist: false
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const orders = trpc.hofDashboard.getMaterialOrderList.useQuery(undefined, t1);
  const [formId, setFormId] = useState();
  const [view, setView] = useState("total");
  const [exportOpen, setExportOpen] = useState(false);
  const exportButton = useRef(null);
  const [reviewing, setReviewing] = useState();
  if (orders.isLoading) {
    let t2;
    if ($[1] !== locale) {
      t2 = translate("loading", locale);
      $[1] = locale;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== t2) {
      t3 = <span className="sr-only">{t2}</span>;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Skeleton className="h-11 w-80 bg-gray-200" />;
      t5 = <Skeleton className="h-96 w-full rounded-xl bg-gray-200" />;
      $[5] = t4;
      $[6] = t5;
    } else {
      t4 = $[5];
      t5 = $[6];
    }
    let t6;
    if ($[7] !== t3) {
      t6 = <div className="space-y-4" role="status">{t3}{t4}{t5}</div>;
      $[7] = t3;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  if (orders.data === undefined) {
    let t2;
    if ($[9] !== locale) {
      t2 = translate(globalThis.navigator.onLine ? "loadError" : "offline", locale);
      $[9] = locale;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    let t3;
    if ($[11] !== t2) {
      t3 = <p>{t2}</p>;
      $[11] = t2;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    let t4;
    if ($[13] !== orders) {
      t4 = () => void orders.refetch();
      $[13] = orders;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== locale) {
      t5 = translate("retry", locale);
      $[15] = locale;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== t4 || $[18] !== t5) {
      t6 = <button type="button" className={SECONDARY_BUTTON_CLASS} onClick={t4}>{t5}</button>;
      $[17] = t4;
      $[18] = t5;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== t3 || $[21] !== t6) {
      t7 = <Panel className="space-y-4 text-base text-green-800">{t3}{t6}</Panel>;
      $[20] = t3;
      $[21] = t6;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    return t7;
  }
  const forms = orders.data;
  let t2;
  if ($[23] !== formId || $[24] !== forms) {
    t2 = forms.find(candidate => candidate.id === formId) ?? forms[0];
    $[23] = formId;
    $[24] = forms;
    $[25] = t2;
  } else {
    t2 = $[25];
  }
  const form = t2;
  if (form === undefined) {
    let t3;
    if ($[26] !== locale) {
      t3 = translate("noMaterialForms", locale);
      $[26] = locale;
      $[27] = t3;
    } else {
      t3 = $[27];
    }
    let t4;
    if ($[28] !== t3) {
      t4 = <Panel><p className="text-base text-green-700">{t3}</p></Panel>;
      $[28] = t3;
      $[29] = t4;
    } else {
      t4 = $[29];
    }
    return t4;
  }
  let t3;
  if ($[30] !== form.hoefe || $[31] !== reviewing) {
    let t4;
    if ($[33] !== reviewing) {
      t4 = hof => hof.hofId === reviewing;
      $[33] = reviewing;
      $[34] = t4;
    } else {
      t4 = $[34];
    }
    t3 = form.hoefe.find(t4);
    $[30] = form.hoefe;
    $[31] = reviewing;
    $[32] = t3;
  } else {
    t3 = $[32];
  }
  const reviewed = t3;
  if (reviewed !== undefined) {
    let t4;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = () => setReviewing(undefined);
      $[35] = t4;
    } else {
      t4 = $[35];
    }
    let t5;
    if ($[36] !== onOpenHof || $[37] !== reviewed.hofId) {
      t5 = () => onOpenHof(reviewed.hofId);
      $[36] = onOpenHof;
      $[37] = reviewed.hofId;
      $[38] = t5;
    } else {
      t5 = $[38];
    }
    let t6;
    if ($[39] !== form || $[40] !== locale || $[41] !== reviewed || $[42] !== t5) {
      t6 = <MaterialOrderReview form={form} hof={reviewed} onBack={t4} onOpenInDashboard={t5} locale={locale} />;
      $[39] = form;
      $[40] = locale;
      $[41] = reviewed;
      $[42] = t5;
      $[43] = t6;
    } else {
      t6 = $[43];
    }
    return t6;
  }
  let t4;
  if ($[44] !== form.id || $[45] !== forms || $[46] !== locale) {
    t4 = forms.length > 1 && <Segmented label={translate("orderForm", locale)} value={form.id} onChange={setFormId} options={forms.map(_temp14)} />;
    $[44] = form.id;
    $[45] = forms;
    $[46] = locale;
    $[47] = t4;
  } else {
    t4 = $[47];
  }
  let t5;
  if ($[48] !== locale) {
    t5 = translate("viewLabel", locale);
    $[48] = locale;
    $[49] = t5;
  } else {
    t5 = $[49];
  }
  let t6;
  if ($[50] !== locale) {
    t6 = translate("viewTotal", locale);
    $[50] = locale;
    $[51] = t6;
  } else {
    t6 = $[51];
  }
  let t7;
  if ($[52] !== t6) {
    t7 = {
      value: "total",
      label: t6
    };
    $[52] = t6;
    $[53] = t7;
  } else {
    t7 = $[53];
  }
  let t8;
  if ($[54] !== locale) {
    t8 = translate("viewPerHof", locale);
    $[54] = locale;
    $[55] = t8;
  } else {
    t8 = $[55];
  }
  let t9;
  if ($[56] !== t8) {
    t9 = {
      value: "perHof",
      label: t8
    };
    $[56] = t8;
    $[57] = t9;
  } else {
    t9 = $[57];
  }
  let t10;
  if ($[58] !== t7 || $[59] !== t9) {
    t10 = [t7, t9];
    $[58] = t7;
    $[59] = t9;
    $[60] = t10;
  } else {
    t10 = $[60];
  }
  let t11;
  if ($[61] !== t10 || $[62] !== t5 || $[63] !== view) {
    t11 = <Segmented label={t5} value={view} onChange={setView} options={t10} />;
    $[61] = t10;
    $[62] = t5;
    $[63] = view;
    $[64] = t11;
  } else {
    t11 = $[64];
  }
  let t12;
  let t13;
  let t14;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn(SECONDARY_BUTTON_CLASS, "@3xl:ml-auto");
    t13 = () => setExportOpen(true);
    t14 = <FileSpreadsheet aria-hidden={true} />;
    $[65] = t12;
    $[66] = t13;
    $[67] = t14;
  } else {
    t12 = $[65];
    t13 = $[66];
    t14 = $[67];
  }
  let t15;
  if ($[68] !== locale) {
    t15 = translate("excelExport", locale);
    $[68] = locale;
    $[69] = t15;
  } else {
    t15 = $[69];
  }
  let t16;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ChevronDown aria-hidden={true} />;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  let t17;
  if ($[71] !== exportOpen || $[72] !== t15) {
    t17 = <button type="button" ref={exportButton} aria-haspopup="dialog" aria-expanded={exportOpen} className={t12} onClick={t13}>{t14}{t15}{t16}</button>;
    $[71] = exportOpen;
    $[72] = t15;
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  let t18;
  if ($[74] !== t11 || $[75] !== t17 || $[76] !== t4) {
    t18 = <div className="flex flex-wrap items-center gap-3">{t4}{t11}{t17}</div>;
    $[74] = t11;
    $[75] = t17;
    $[76] = t4;
    $[77] = t18;
  } else {
    t18 = $[77];
  }
  let t19;
  if ($[78] !== exportOpen || $[79] !== form || $[80] !== forms || $[81] !== locale) {
    t19 = <ExportSheet open={exportOpen} onOpenChange={setExportOpen} anchor={exportButton} forms={forms} current={form} locale={locale} />;
    $[78] = exportOpen;
    $[79] = form;
    $[80] = forms;
    $[81] = locale;
    $[82] = t19;
  } else {
    t19 = $[82];
  }
  const t20 = view === "total" && "@4xl:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)]";
  let t21;
  if ($[83] !== t20) {
    t21 = cn("grid items-start gap-6", t20);
    $[83] = t20;
    $[84] = t21;
  } else {
    t21 = $[84];
  }
  let t22;
  if ($[85] !== form || $[86] !== locale || $[87] !== view) {
    t22 = <Panel className="min-w-0 overflow-clip p-0 @xl:p-0">{view === "total" ? <TotalTable form={form} onOpen={setReviewing} locale={locale} /> : <PerHofTable form={form} onOpen={setReviewing} locale={locale} />}</Panel>;
    $[85] = form;
    $[86] = locale;
    $[87] = view;
    $[88] = t22;
  } else {
    t22 = $[88];
  }
  const t23 = view === "perHof" && "grid items-start gap-6 space-y-0 @3xl:grid-cols-3";
  let t24;
  if ($[89] !== t23) {
    t24 = cn(t23);
    $[89] = t23;
    $[90] = t24;
  } else {
    t24 = $[90];
  }
  let t25;
  if ($[91] !== form || $[92] !== locale || $[93] !== t24) {
    t25 = <SidePanels form={form} onOpen={setReviewing} locale={locale} className={t24} />;
    $[91] = form;
    $[92] = locale;
    $[93] = t24;
    $[94] = t25;
  } else {
    t25 = $[94];
  }
  let t26;
  if ($[95] !== t21 || $[96] !== t22 || $[97] !== t25) {
    t26 = <div className={t21}>{t22}{t25}</div>;
    $[95] = t21;
    $[96] = t22;
    $[97] = t25;
    $[98] = t26;
  } else {
    t26 = $[98];
  }
  let t27;
  if ($[99] !== t18 || $[100] !== t19 || $[101] !== t26) {
    t27 = <div className="space-y-6">{t18}{t19}{t26}</div>;
    $[99] = t18;
    $[100] = t19;
    $[101] = t26;
    $[102] = t27;
  } else {
    t27 = $[102];
  }
  return t27;
};
function _temp(sum, order) {
  return sum + order.quantity;
}
function _temp2(t0) {
  const {
    section
  } = t0;
  return section !== undefined;
}
function _temp3(hof, column) {
  return column === "hof" ? hof.hofName : hof.quantities[column.slice(5)] ?? 0;
}
function _temp4(t0, index) {
  const {
    section: section_0,
    items
  } = t0;
  return <th key={section_0 ?? `section-${index}`} scope="colgroup" colSpan={items.length} className="border-line border-l px-3 pt-3 text-left font-bold">{section_0}</th>;
}
function _temp5() {
  return new Set(["total", "perHof", "remarks"]);
}
function _temp6(form) {
  return form.id;
}
function _temp7(hof) {
  return hof.orderedAt !== undefined;
}
function _temp8(hof_0) {
  return hof_0.orderedAt === undefined;
}
function _temp9(hof_1) {
  return hof_1.changedSinceExport;
}
function _temp10(a, b) {
  return (b.orderedAt ?? "").localeCompare(a.orderedAt ?? "");
}
function _temp11(version) {
  return version.remarks;
}
function _temp12(hof_3) {
  return <li key={hof_3.hofId}>{hof_3.hofName}</li>;
}
function _temp13(remark, index) {
  return <p key={index} className="text-sm whitespace-pre-line text-green-800"><span className="text-green-600">{remark.label}: </span>{remark.text}</p>;
}
function _temp14(candidate_0) {
  return {
    value: candidate_0.id,
    label: candidate_0.title
  };
}